import { useEffect, useState } from 'react'

import { ActionIcon, Divider, Select, Tooltip } from '@mantine/core'
import { DatesProvider } from '@mantine/dates'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useQuery, useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DocumentPreview from 'src/components/Overlays/DocumentPreview/DocumentPreview'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_ADVERSE_ACTION_CASES_BY_APPLICANT } from 'src/graphql/adverseActions.graphql'
import {
  GET_APPLICATION_BY_APPLICANT_ID,
  UPDATE_APPLICATION,
} from 'src/graphql/application.graphql'
import { GET_COMPANY } from 'src/graphql/companies.graphql'
import { ApplicationStatus } from 'src/graphql/types/applications'
import IconShareOpen from 'src/icons/IconShareOpen'
import { DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS } from 'src/lib/voeVerificationPeriod'

import AccidentsCard from './components/AccidentsCard'
import AddressInformationCard from './components/AddressInformationCard'
import AdverseActionAlerts from './components/AdverseActionAlerts/AdverseActionAlerts'
import AiFlagsPill from './components/AiFlagsPill/AiFlagsPill'
import CompanyQuestionsCard from './components/CompanyQuestionsCard'
import ConsentCard from './components/ConsentCard'
import EmploymentCard from './components/EmploymentCard/EmploymentCard'
import EmploymentSummaryCard from './components/EmploymentSummaryCard/EmploymentSummary'
import EquipmentCard from './components/EquipmentCard/EquipmentCard'
import JobInformationCard from './components/JobInformationCard/JobInformationCard'
import LicenseInformationCard from './components/LicenseInformationCard/LicenseInformationCard'
import ApplicantSharingModal from './components/Modals/ApplicantSharingModal'
import PersonalInformationCard from './components/PersonalInformationCard'
import PreviousLicensesCard from './components/PreviousLicensesCard'
import TwicInformationCard from './components/TwicInformationCard/TwicInformationCard'
import {
  getAddressInformation,
  getJobInformation,
  getLicenseInformation,
  getPreviousLicenses,
  getTwicInformation,
  getPersonalInformation,
  getAccidentInformation,
  getOwnerOperatorEquipment,
} from './generateData'

interface ApplicationTabProps {
  applicantId: string
  onRender: any
  onApplicantNotFound: () => void
}

const ApplicationTab = ({
  applicantId,
  onRender,
  onApplicantNotFound,
}: ApplicationTabProps) => {
  const { currentUser } = useAuth()
  const [opened, { open, close }] = useDisclosure(false)
  const [updatedItems] = useState({})

  const { data: companyData } = useQuery(GET_COMPANY, {
    variables: { id: currentUser?.companyId },
    skip: !currentUser?.companyId,
  })
  const verificationPeriodMonths =
    companyData?.company?.voeVerificationPeriodMonths ??
    DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS
  const [documentForPreview, setDocumentForPreview] = useState(null)

  const { data: applicationResponse } = useQuery(
    GET_APPLICATION_BY_APPLICANT_ID,
    {
      variables: {
        applicantId,
      },
      onError: () => {
        toast('Unable to get application, please try again.', 'error')
      },
    }
  )

  const application = applicationResponse?.applicationByApplicantId

  const { data: adverseActionsResponse } = useQuery(
    GET_ADVERSE_ACTION_CASES_BY_APPLICANT,
    {
      variables: {
        applicantId,
      },
      skip: !application,
      onError: () => {
        toast('Unable to get adverse actions, please try again.', 'error')
      },
    }
  )

  const [updateApplication, { loading }] = useMutation(UPDATE_APPLICATION, {
    variables: {
      id: applicationResponse?.applicationByApplicantId?.applicationId,
      input: { ...updatedItems },
    },
    refetchQueries: [
      {
        query: GET_APPLICATION_BY_APPLICANT_ID,
        variables: {
          applicantId,
        },
      },
      ...onRender,
    ],
    onCompleted: () => {
      toast('Application updated successfully', 'success')
    },
    onError: () => {
      toast('Unable to update application, please try again.', 'error')
    },
  })

  const updateStatus = (value) => {
    updateApplication({
      variables: {
        id: applicationResponse?.applicationByApplicantId?.applicationId,
        input: {
          status: value,
        },
      },
    })
  }

  const handleUpdateApplication = async (value) => {
    return await updateApplication({
      variables: {
        id: applicationResponse?.applicationByApplicantId?.applicationId,
        input: {
          ...value,
        },
      },
    })
  }

  const handlePreview = (consent) => {
    setDocumentForPreview(consent.document)
  }

  // Check if applicant has any adverse actions
  const adverseActionCases =
    adverseActionsResponse?.adverseActionCasesByApplicant || []

  useEffect(() => {
    if (applicationResponse && !application) {
      onApplicantNotFound()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationResponse, application])

  if (!applicationResponse) return null

  if (!application) {
    return null
  }

  // Map VoE status from GraphQL enum to display label
  const mapVoeStatus = (status?: string) => {
    const map: Record<string, string> = {
      COMPLETED: 'Completed',
      REQUESTED: 'Requested',
      IN_PROGRESS: 'In Progress',
      PENDING_REVIEW: 'In Progress',
      NOT_STARTED: 'Not Started',
      CANCELED: 'Canceled',
      DECLINED: 'Declined',
    }
    return status ? map[status] : undefined
  }

  const employmentExperiences =
    application?.employmentExperiences?.map((experience) => ({
      ...experience,
      startDate: experience?.startDate
        ? new Date(experience.startDate)
        : undefined,
      endDate: experience?.endDate ? new Date(experience.endDate) : undefined,
      voeStatus: mapVoeStatus(experience?.verificationOfEmployment?.status),
      hasConsent: experience?.isUnemployment
        ? undefined
        : experience?.verificationOfEmployment
        ? true // VoE exists = consent was given
        : false, // No VoE record = no consent yet
    })) || []

  return (
    <DatesProvider settings={{ timezone: 'UTC' }}>
      <div className="flex h-full flex-col gap-5">
        {opened && (
          <ApplicantSharingModal
            isModalOpen={opened}
            onModalClose={close}
            applicationId={application.applicationId}
          />
        )}
        {documentForPreview ? (
          <DocumentPreview
            documentId={documentForPreview.documentId}
            applicantId={applicantId}
            onBack={() => setDocumentForPreview(null)}
            showActions={false}
          />
        ) : (
          <div className="flex h-[88%] flex-col gap-4 overflow-auto">
            <div>
              <div className="flex flex-row items-center gap-4 py-2">
                <div className="text-lg font-semibold text-doubleNickel-black">
                  Application
                </div>
                <Select
                  value={application?.status}
                  onChange={(value) => updateStatus(value)}
                  data={[
                    {
                      label: 'Not Started',
                      value: ApplicationStatus.NOT_STARTED,
                    },
                    {
                      label: 'In Progress',
                      value: ApplicationStatus.IN_PROGRESS,
                    },
                    {
                      label: 'Pending Review',
                      value: ApplicationStatus.PENDING_REVIEW,
                    },
                    { label: 'Completed', value: ApplicationStatus.COMPLETED },
                  ]}
                  classNames={{
                    root: 'w-40',
                    option: 'text-xs',
                    input: `rounded-lg text-xs font-semibold ${
                      application?.status === ApplicationStatus.IN_PROGRESS &&
                      'bg-doubleNickel-brand-25 border border-doubleNickel-brand-500 text-doubleNickel-brand-500 '
                    }
                ${
                  application?.status === ApplicationStatus.PENDING_REVIEW &&
                  'bg-doubleNickel-warning-50 border border-doubleNickel-warning-500 text-doubleNickel-warning-600 '
                }
                ${
                  application?.status === ApplicationStatus.COMPLETED &&
                  'bg-doubleNickel-success-50 border border-doubleNickel-success-600 text-doubleNickel-success-600 '
                }`,
                  }}
                  allowDeselect={false}
                />
                <div className="text-xs font-medium text-doubleNickel-gray-600">
                  Last updated:{' '}
                  {dayjs(application?.updatedAt).format(
                    'HH:mm:ss on MM/DD/YYYY'
                  )}
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {/* Gate on a loaded application so the pill never compares
                      against an undefined license mid-fetch (would falsely flag
                      saved values as "Not set"). */}
                  {application && (
                    <AiFlagsPill
                      applicantId={applicantId}
                      license={application.license}
                      disclosedAccidentCount={
                        application.accidents?.length ?? 0
                      }
                    />
                  )}
                  <Tooltip label="Share">
                    <ActionIcon variant="subtle" onClick={open}>
                      <IconShareOpen className="max-h-5 fill-none stroke-doubleNickel-gray-700" />
                    </ActionIcon>
                  </Tooltip>
                </div>
              </div>
              <Divider />
            </div>
            <div className="flex flex-col gap-4 overflow-auto">
              <AdverseActionAlerts
                applicantId={applicantId}
                adverseActionCases={adverseActionCases}
              />

              <JobInformationCard
                {...getJobInformation(application)}
                handleUpdateApplication={handleUpdateApplication}
              />
              <PersonalInformationCard
                {...getPersonalInformation(application)}
                handleUpdateApplication={handleUpdateApplication}
              />
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-3">
                  <LicenseInformationCard
                    {...getLicenseInformation(application)}
                    handleUpdateApplication={handleUpdateApplication}
                    applicantId={applicantId}
                  />
                </div>
                <div className="col-span-1">
                  <TwicInformationCard
                    {...getTwicInformation(application)}
                    handleUpdateApplication={handleUpdateApplication}
                  />
                </div>
              </div>

              {application?.employmentExperiences.length > 0 && (
                <EmploymentSummaryCard application={application} />
              )}
              <EmploymentCard
                employmentExperiences={employmentExperiences}
                handleUpdateApplication={handleUpdateApplication}
                applicationId={application?.applicationId}
                applicantId={applicantId}
                verificationPeriodMonths={verificationPeriodMonths}
              />
              <PreviousLicensesCard
                {...getPreviousLicenses(application)}
                handleUpdateApplication={handleUpdateApplication}
              />
              <AddressInformationCard
                {...getAddressInformation(application)}
                handleUpdateApplication={handleUpdateApplication}
              />
              <AccidentsCard
                {...getAccidentInformation(application)}
                applicantId={applicantId}
                handleUpdateApplication={handleUpdateApplication}
              />
              <EquipmentCard
                {...getOwnerOperatorEquipment(application)}
                handleUpdateApplication={handleUpdateApplication}
              />
              <CompanyQuestionsCard
                applicationId={application?.applicationId}
                applicantId={applicantId}
                jobListingId={application?.applicant?.jobListing?.jobListingId}
                questionResponses={application?.questionResponses}
                onUpdate={() => {
                  // Refetch application data after update
                }}
              />
              <ConsentCard
                consents={application?.consents}
                onPreview={handlePreview}
              />
            </div>
          </div>
        )}
      </div>
    </DatesProvider>
  )
}

export default ApplicationTab
